// Cloud save. Legacy: generateThumbnail/cloudSaveDesign/uniqueDesignName (L8875-8967).
// canvas_json stores the serialized object (jsonb column), not a string.
import { supabase } from './../auth/supabase'
import { getCanvas, state } from './../state'
import { CANVAS_CUSTOM_PROPS } from './../history'
import { getDesignLimit } from './../billing'

export function generateThumbnail(): string | null {
  try {
    return getCanvas().toDataURL({ format: 'png', quality: 0.5, multiplier: 0.3 })
  } catch {
    return null
  }
}

function canvasJson(): unknown {
  return getCanvas().toObject(CANVAS_CUSTOM_PROPS)
}

/** Dedupe a design name against the user's existing designs ("Name", "Name (1)", …). */
export async function uniqueDesignName(baseName: string): Promise<string> {
  if (!state.currentUser) return baseName
  const { data } = await supabase.from('designs').select('name').eq('user_id', state.currentUser.id)
  const existing = new Set((data ?? []).map((d: { name: string }) => d.name))
  if (!existing.has(baseName)) return baseName
  let n = 1
  while (existing.has(`${baseName} (${n})`)) n++
  return `${baseName} (${n})`
}

export type SaveResult =
  | { ok: true; id: string; name: string; action: 'insert' | 'update' }
  | { ok: false; reason: 'signin' | 'limit' | 'error'; message?: string }

/** Insert or update the current design (legacy cloudSaveDesign). Name is passed in
 *  (no window.prompt). Caller handles the signin/limit reasons in the UI. */
export async function cloudSaveDesign(name = state.currentDesignName || 'My Label'): Promise<SaveResult> {
  if (!state.currentUser) return { ok: false, reason: 'signin' }

  const payload = {
    canvas_json: canvasJson(),
    thumbnail: generateThumbnail(),
    label_size: state.currentSize,
    orientation: state.orientation,
    batch_headers: state.batchHeaders.length ? state.batchHeaders : null,
    batch_data: state.batchData.length ? state.batchData : null,
  }

  if (state.currentDesignId) {
    const { error } = await supabase
      .from('designs')
      .update({ ...payload, updated_at: new Date().toISOString() })
      .eq('id', state.currentDesignId)
    if (error) return { ok: false, reason: 'error', message: error.message }
    return { ok: true, id: state.currentDesignId, name: state.currentDesignName, action: 'update' }
  }

  // New design — enforce the free-tier cap.
  const { count } = await supabase
    .from('designs')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', state.currentUser.id)
  if ((count ?? 0) >= getDesignLimit()) return { ok: false, reason: 'limit' }

  const finalName = await uniqueDesignName(name.substring(0, 100))
  const { data, error } = await supabase
    .from('designs')
    .insert({ user_id: state.currentUser.id, name: finalName, ...payload })
    .select('id')
    .single()
  if (error || !data) return { ok: false, reason: 'error', message: error?.message }

  state.currentDesignId = data.id
  state.currentDesignName = finalName
  return { ok: true, id: data.id, name: finalName, action: 'insert' }
}
